import React from 'react';
import ActionBadge from './ActionBadge';

/**
 * AuditLogRow Component
 * 
 * Renders individual audit log table row with consistent styling using OM design tokens.
 * Extracts row rendering logic from the monolithic component while preserving
 * exact same data display format and user experience.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.log - Audit log entry object
 * @param {string} props.log.id - Unique log entry ID
 * @param {string} props.log.createdAt - ISO date string
 * @param {Object} props.log.user - User object (username, email) or null for system
 * @param {string} props.log.action - Action type
 * @param {string} props.log.claimId - Associated claim ID or null
 * @param {string} props.log.details - Additional details or null
 */
const AuditLogRow = ({ log }) => {
  /**
   * Format date and time for display
   * Maintains exact same formatting as legacy component
   */
  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  /**
   * Render user information cell
   * Shows username/email or "System" for system actions
   */
  const renderUserCell = (user) => {
    if (user) {
      return (
        <div>
          <div className="font-medium text-gray-900">{user.username}</div>
          <div className="text-gray-500 text-xs">{user.email}</div>
        </div>
      );
    }
    return (
      <span className="text-gray-400 italic">System</span>
    );
  };

  /**
   * Render claim ID cell
   * Shows formatted claim ID or dash for no claim
   */
  const renderClaimIdCell = (claimId) => {
    if (claimId) {
      return (
        <span className="font-mono bg-gray-50 px-2 py-1 rounded-sm text-sm border border-gray-200">
          #{claimId}
        </span>
      );
    }
    return (
      <span className="text-gray-400">-</span>
    );
  };

  /**
   * Render details cell with truncation
   * Shows details with tooltip on hover or dash for no details
   */
  const renderDetailsCell = (details) => {
    if (details) {
      return (
        <div className="truncate max-w-xs" title={details}>
          {details}
        </div>
      );
    }
    return (
      <span className="text-gray-400">-</span>
    );
  };

  return (
    <tr className="hover:bg-gray-50 transition-colors duration-150">
      {/* Timestamp Cell */}
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
        {formatDateTime(log.createdAt)}
      </td>

      {/* User Cell */}
      <td className="px-6 py-4 whitespace-nowrap text-sm">
        {renderUserCell(log.user)}
      </td>

      {/* Action Cell with Badge */}
      <td className="px-6 py-4 whitespace-nowrap">
        <ActionBadge action={log.action} />
      </td>

      {/* Claim ID Cell */}
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
        {renderClaimIdCell(log.claimId)}
      </td>

      {/* Details Cell */}
      <td className="px-6 py-4 text-sm text-gray-900">
        {renderDetailsCell(log.details)}
      </td>
    </tr>
  );
};

export default AuditLogRow;